import React, { useState, useEffect } from 'react';
import { X, MessageSquareHeart } from 'lucide-react';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { founding } from '../utils/api';

const VIOLET = '#8B5CF6';
const QUESTIONS = [
    { key: 'q3', label: 'What would you improve or change about the assessment experience or the platform so far?' },
    { key: 'q2', label: 'What\u2019s the most frustrating thing about how you\u2019ve been assessed or hired in the past?' },
    { key: 'q1', label: 'What did your Talent Card reveal about you that a CV never would?' },
    { key: 'q4', label: 'Anything else you want us to know?' }
];

/**
 * Floating "Give Feedback" button + modal, always available on the
 * candidate dashboard once their assessment is complete. Deliberately
 * decoupled from Founding Member number assignment — that happens
 * automatically at assessment completion (see assessment_endpoints.py)
 * and is displayed via the small FoundingBadge next to the candidate's
 * name on their Talent Card, not here. This component's only job is
 * collecting feedback and getting it to the Sampler team by email.
 */
const FoundingMemberFAB = () => {
    const [open, setOpen] = useState(false);
    const [stage, setStage] = useState('form'); // 'form' | 'thanks'
    const [responses, setResponses] = useState({ q1: '', q2: '', q3: '', q4: '' });
    const [submitting, setSubmitting] = useState(false);

    const openModal = () => {
        setStage('form');
        setResponses({ q1: '', q2: '', q3: '', q4: '' });
        setOpen(true);
    };

    const closeModal = () => setOpen(false);

    // Auto-open the feedback flow when arriving from the feedback-nudge
    // email's deep link (?feedback=open) — same state as clicking the FAB.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('feedback') === 'open') {
            openModal();
            params.delete('feedback');
            const rest = params.toString();
            window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
        }
    }, []);

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await founding.submit(responses);
            setStage('thanks');
        } catch (err) {
            console.error('Feedback submit error:', err);
            toast.error('Something went wrong submitting your feedback. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={openModal}
                data-testid="feedback-fab"
                style={{
                    position: 'fixed',
                    right: '24px',
                    bottom: '24px',
                    zIndex: 60,
                    background: VIOLET,
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '999px',
                    padding: '14px 22px',
                    fontFamily: 'Unbounded, sans-serif',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    letterSpacing: '0.01em',
                    cursor: 'pointer',
                    boxShadow: '0 10px 30px rgba(139, 92, 246, 0.45), 0 4px 12px rgba(139, 92, 246, 0.3)',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <MessageSquareHeart className="h-4 w-4" />
                    <span>Give Feedback</span>
                </span>
            </button>

            {open && (
                <div
                    role="dialog"
                    aria-modal="true"
                    onClick={closeModal}
                    data-testid="founding-modal-overlay"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(10, 10, 10, 0.55)',
                        zIndex: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        data-testid="founding-modal"
                        style={{
                            background: '#FFFFFF',
                            borderRadius: '20px',
                            width: '100%',
                            maxWidth: '640px',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.25)'
                        }}
                    >
                        {stage === 'form' && (
                            <div style={{ padding: '32px 36px 28px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                                    <div>
                                        <div style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: VIOLET, marginBottom: 6 }}>
                                            Founding 1000 Feedback
                                        </div>
                                        <h2 style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '1.75rem', fontWeight: 800, color: '#0A0A0A', margin: 0, lineHeight: 1.15 }}>
                                            Shape What We&rsquo;re Building
                                        </h2>
                                    </div>
                                    <button type="button" onClick={closeModal} aria-label="Close" data-testid="founding-modal-close"
                                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}>
                                        <X className="h-5 w-5 text-slate-400" />
                                    </button>
                                </div>
                                <p style={{ fontFamily: 'Barlow, sans-serif', color: '#475569', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: 24 }}>
                                    Your feedback shapes what comes next.
                                </p>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                                    {QUESTIONS.map((q, idx) => (
                                        <div key={q.key}>
                                            <label htmlFor={`founding-${q.key}`} style={{ display: 'block', fontFamily: 'Unbounded, sans-serif', fontSize: '0.8rem', fontWeight: 700, color: '#0A0A0A', marginBottom: 8 }}>
                                                Q{idx + 1}. {q.label}
                                            </label>
                                            <Textarea
                                                id={`founding-${q.key}`}
                                                data-testid={`founding-${q.key}-input`}
                                                value={responses[q.key]}
                                                onChange={(e) => setResponses(prev => ({ ...prev, [q.key]: e.target.value }))}
                                                placeholder="Type your answer..."
                                                rows={3}
                                                style={{ fontFamily: 'Barlow, sans-serif' }}
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 28, gap: 12, flexWrap: 'wrap' }}>
                                    <button type="button" onClick={closeModal} disabled={submitting} data-testid="founding-skip-btn"
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#64748B',
                                            fontFamily: 'Barlow, sans-serif',
                                            fontSize: '0.95rem',
                                            fontWeight: 600,
                                            textDecoration: 'underline',
                                            cursor: submitting ? 'not-allowed' : 'pointer',
                                            padding: '8px 4px'
                                        }}>
                                        Maybe later
                                    </button>
                                    <button type="button" onClick={handleSubmit} disabled={submitting} data-testid="founding-submit-btn"
                                        style={{
                                            background: VIOLET,
                                            color: '#FFFFFF',
                                            border: 'none',
                                            borderRadius: '999px',
                                            padding: '14px 32px',
                                            fontFamily: 'Unbounded, sans-serif',
                                            fontSize: '0.95rem',
                                            fontWeight: 700,
                                            cursor: submitting ? 'not-allowed' : 'pointer',
                                            opacity: submitting ? 0.7 : 1,
                                            boxShadow: '0 8px 22px rgba(139, 92, 246, 0.35)'
                                        }}>
                                        {submitting ? 'Submitting...' : 'Submit Feedback'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {stage === 'thanks' && (
                            <div style={{ padding: '48px 36px 40px', textAlign: 'center' }} data-testid="founding-confirmation">
                                <div style={{
                                    width: 80, height: 80, margin: '0 auto 20px', borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: 'linear-gradient(135deg, #A78BFA, #8B5CF6)',
                                    boxShadow: '0 10px 30px rgba(139, 92, 246, 0.45)'
                                }}>
                                    <MessageSquareHeart className="h-9 w-9 text-white" />
                                </div>
                                <div style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: VIOLET, marginBottom: 10 }}>
                                    Thank You
                                </div>
                                <p style={{ fontFamily: 'Barlow, sans-serif', fontSize: '1.05rem', lineHeight: 1.6, color: '#1E293B', maxWidth: 480, margin: '0 auto 28px' }}>
                                    Your feedback has been sent straight to the Sampler team. We read every word &mdash; thank you for helping shape what comes next.
                                </p>
                                <button type="button" onClick={closeModal} data-testid="founding-confirmation-close"
                                    style={{
                                        background: VIOLET,
                                        color: '#FFFFFF',
                                        border: 'none',
                                        borderRadius: '999px',
                                        padding: '14px 36px',
                                        fontFamily: 'Unbounded, sans-serif',
                                        fontSize: '0.95rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        boxShadow: '0 8px 22px rgba(139, 92, 246, 0.35)'
                                    }}>
                                    Close
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default FoundingMemberFAB;
